import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, ElementRef, OnDestroy, WritableSignal, signal } from '@angular/core';

// Define an interface for the structure of our scroll data
export interface ScrollParentInfo {
	name: string;
	scrollTop: number;
}

@Component({
	selector: 'scroll-tracker-widget',
	standalone: true,
	imports: [CommonModule], // For *ngFor
	template: `
    <div class="scroll-tracker-output">
      <div *ngFor="let item of scrollData()">
        {{ item.name }}: {{ item.scrollTop }}
      </div>
    </div>
  `,
	styles: [`
    .scroll-tracker-output {
      background-color: rgba(20, 20, 40, 0.8);
      color: #e0e0e0;
      border: 1px solid rgba(255, 255, 255, 0.2);
      border-radius: 5px;
      padding: 6px 10px;
      box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      z-index: 1000;
      font-family: monospace;
      font-size: 11px;
      line-height: 1.3;
      backdrop-filter: blur(4px);
      -webkit-backdrop-filter: blur(4px);
    }
  `]
})
export class ScrollTrackerWidget implements AfterViewInit, OnDestroy {

	// A Signal to hold the array of scroll information.
	public scrollData: WritableSignal<ScrollParentInfo[]> = signal([]);

	// Store all ancestors and the window object for attaching/detaching listeners.
	private listenedElements: (HTMLElement | Window)[] = [];
	// A single listener function that updates all data.
	private readonly scrollListener = () => this.updateScrollData();

	constructor(private elementRef: ElementRef) { }

	ngAfterViewInit(): void {
		const ancestors: HTMLElement[] = [];
		let currentElement = this.elementRef.nativeElement.parentElement;

		// 1. Walk up the DOM tree and collect all parent elements.
		while (currentElement) {
			ancestors.push(currentElement);
			currentElement = currentElement.parentElement;
		}

		// 2. Add the window object to listen for the main page scroll.
		this.listenedElements = [...ancestors, window].reverse();

		// 3. Attach a single, shared listener to all elements.
		this.listenedElements.forEach(el => {
			el.addEventListener('scroll', this.scrollListener, { passive: true });
		});

		// 4. Perform the initial data population.
		this.updateScrollData();
	}

	ngOnDestroy(): void {
		// Clean up: remove the shared listener from all elements.
		this.listenedElements.forEach(el => {
			el.removeEventListener('scroll', this.scrollListener);
		});
	}

	/**
	 * Re-calculates the scrollTop for every ancestor and updates the signal.
	 * This is triggered by a scroll event on ANY ancestor.
	 */
	private updateScrollData(): void {
		const newScrollData = (this.listenedElements as (HTMLElement | Window)[]).map(el => {
			if (el instanceof Window) {
				return {
					name: 'window',
					// Use `document.documentElement` for modern browsers, with a fallback.
					scrollTop: Math.round(document.documentElement.scrollTop || document.body.scrollTop)
				};
			} else {
				return {
					name: this.getElementName(el),
					scrollTop: Math.round(el.scrollTop)
				};
			}
		});

		this.scrollData.set(newScrollData);
	}

	/**
	 * Generates a descriptive name for an element (e.g., "div#main.content").
	 */
	private getElementName(element: HTMLElement): string {
		const tagName = element.tagName.toLowerCase();
		const id = element.id ? `#${element.id}` : '';
		// Prevent extremely long class lists from cluttering the display
		const classes = element.className ? `.${String(element.className).split(' ').slice(0, 3).join('.')}` : '';
		return `${tagName}${id}${classes}`;
	}
}